/**
 * KoredorCommandDashboard — Koredor-only structural pass over the Command Dashboard.
 *
 * SCOPE: UI restructure only. Every piece of business logic, every intelligence
 * surface, and every action from the Classic dashboard is preserved:
 *   • Push Mode Test Bench (QA tool)
 *   • What Needs Attention strip (live intelligence)
 *   • Growth Performance — channel ranking with CPA, lead quality, status
 *   • Revenue Quality — Won / Pending / Net split with conversion %
 *   • Rep Performance — leaderboard with high-fit conversion + insight signal
 *   • Booking Efficiency — travel, long-distance, clustering
 *   • System Recommendations — the guidance layer (positive / caution prompts)
 *   • Revenue Leak Panel — leak detection
 *   • Live Actions — Push Mode resolution for missing results, follow-ups, reschedules
 *
 * What changes is *form*, not *function*:
 *   #1 One hero surface (Live Actions + Recommendations together) instead of
 *      a 4×N grid of equal-weight cards.
 *   #2 Hairline-divided stat strip instead of a row of metric tiles.
 *   #3 Icons demoted to status dots and primary CTAs only — no decorative
 *      icons in section headers, table headers, or inline labels.
 *   #5 Inline resolution on the top action row — no toast soup.
 *   #7 One primary action on the page (top attention row's solid button).
 *
 * Toggling Classic restores the original layout exactly.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { cn } from "@/lib/utils";
import { SalesFunnelBreakdown } from "@/components/dashboard/SalesFunnelBreakdown";
import { useFinancialPermissions } from "@/hooks/useFinancialPermissions";
import { useCurrentRep } from "@/hooks/useCurrentRep";
import { getOpportunityDisplayValue } from "@/data/opportunityRevenueSelectors";
import { getRepPerformanceSummary } from "@/data/appointmentResultSelectors";
import { computeOutcomeMetrics } from "@/data/scheduling/outcomeMetrics";
import { LogInteractionModal } from "@/components/quick-actions/LogInteractionModal";
import { RescheduleModal } from "@/components/quick-actions/RescheduleModal";
import {
  RecommendationActionModal,
  type RecActionMode,
  type RecActionContext,
} from "@/components/dashboard/RecommendationActionModal";
import { useChannelEvents, isRecommendationDismissed } from "@/data/channelRelationship";
import { FollowUpTaskService } from "@/services/follow-up-task.service";
import { supabase } from "@/lib/supabase";
import { isUuidLike, resolveProfileDisplayName } from "@/lib/profileDisplayName";

import { WhatNeedsAttentionStrip } from "@/components/WhatNeedsAttentionStrip";
import { SectionHeader, StatStrip, type StatItem } from "@/components/dashboard/primitives";
import { DismissedRecsDrawer } from "@/components/dashboard/DismissedRecsDrawer";
// PushModeTestBench is intentionally NOT imported here.
// It's a dashed-border QA scaffold — useful in Classic for testing push chains,
// but visually breaks the cockpit aesthetic. Toggle to Classic to use it.
import type { Job } from "@/data/types";
import { toast } from "sonner";
import { getJobDisplayValue } from "@/data/jobPricingSelectors";

const fmt = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `$${(n / 1_000).toFixed(0)}K` : `$${Math.round(n)}`;

type Period = "today" | "week" | "month";
type ChannelStatus = "Healthy" | "Needs Attention" | "Underperforming";
type Clustering = "High" | "Medium" | "Low";

function normalizeDisplayLabel(value: string | null | undefined, fallback = "Unassigned"): string {
  const v = String(value ?? "").trim();
  if (!v) return fallback;
  const lc = v.toLowerCase();
  if (lc === "unknown" || lc === "n/a" || lc === "na" || lc === "null" || lc === "undefined") {
    return fallback;
  }
  return v;
}

export default function KoredorCommandDashboard() {
  const { appointmentResults, salesOpportunities, followUpTasks, jobs, installerWrapRecords, setFollowUpTasks } = useAppData();
  const { repName: CURRENT_REP } = useCurrentRep();
  const fin$ = useFinancialPermissions();
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>("month");

  // ── Push Mode dispatcher state ──
  const [logCustomerName, setLogCustomerName] = useState<string | null>(null);
  const [logRelatedJobId, setLogRelatedJobId] = useState<string | undefined>(undefined);
  const [logFollowUpId, setLogFollowUpId] = useState<string | null>(null);
  const [rescheduleJob, setRescheduleJob] = useState<Job | null>(null);
  const [repNameById, setRepNameById] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    let cancelled = false;
    const loadRepNames = async () => {
      const repIds = Array.from(
        new Set(
          [
            ...salesOpportunities.map((o) => String(o.assignedRep ?? "").trim()),
            ...appointmentResults.map((r) => String(r.assignedRep ?? "").trim()),
            ...followUpTasks.map((t) => String(t.assignedTo ?? "").trim()),
          ].filter((v) => isUuidLike(v)),
        ),
      );
      if (repIds.length === 0) {
        setRepNameById(new Map());
        return;
      }
      const [byIdRes, byAuthIdRes] = await Promise.all([
        supabase
          .from("profiles")
          .select("id, auth_user_id, full_name, first_name, last_name")
          .in("id", repIds),
        supabase
          .from("profiles")
          .select("id, auth_user_id, full_name, first_name, last_name")
          .in("auth_user_id", repIds),
      ]);
      if (cancelled) return;
      const map = new Map<string, string>();
      const rows = [...(byIdRes.data ?? []), ...(byAuthIdRes.data ?? [])];
      for (const row of rows) {
        const first = String(row.first_name ?? "").trim();
        const last = String(row.last_name ?? "").trim();
        const fullName = String(row.full_name ?? "").trim() || [first, last].filter(Boolean).join(" ").trim();
        const id = String(row.id ?? "").trim();
        const authUserId = String(row.auth_user_id ?? "").trim();
        if (!fullName) continue;
        if (id) map.set(id, fullName);
        if (authUserId) map.set(authUserId, fullName);
      }
      setRepNameById(map);
    };
    void loadRepNames();
    return () => {
      cancelled = true;
    };
  }, [appointmentResults, followUpTasks, salesOpportunities]);

  const resolveRepName = (rep: string | null | undefined) =>
    normalizeDisplayLabel(resolveProfileDisplayName(rep, repNameById, "Unassigned"), "Unassigned");

  const scopedOpps = fin$.scopedToOwn
    ? salesOpportunities.filter((o) => resolveRepName(o.assignedRep) === CURRENT_REP)
    : salesOpportunities;
  const scopedResults = fin$.scopedToOwn
    ? appointmentResults.filter((r) => resolveRepName(r.assignedRep) === CURRENT_REP)
    : appointmentResults;
  const scopedFollowUps = fin$.scopedToOwn
    ? followUpTasks.filter((t) => resolveRepName(t.assignedTo) === CURRENT_REP)
    : followUpTasks;

  const periodLabel = period === "today" ? "Today" : period === "week" ? "Last 7 days" : "Last 30 days";

  const bounds = useMemo(() => {
    const d = new Date();
    if (period === "today") d.setHours(0, 0, 0, 0);
    else if (period === "week") { d.setDate(d.getDate() - 7); d.setHours(0, 0, 0, 0); }
    else { d.setDate(d.getDate() - 30); d.setHours(0, 0, 0, 0); }
    return d.toISOString();
  }, [period]);

  const getOppDate = (o: {
    stageHistory?: { changedAt: string }[];
    updatedAt?: string;
    createdAt?: string;
    appointment_time?: string;
  }) => {
    const hist = o.stageHistory ?? [];
    if (hist.length > 0) return hist[hist.length - 1].changedAt;
    return String(o.updatedAt ?? o.createdAt ?? o.appointment_time ?? "");
  };

  const filteredOpps = useMemo(() => scopedOpps.filter((o) => getOppDate(o) >= bounds), [scopedOpps, bounds]);
  const filteredResults = useMemo(() => {
    const oppById = new Map(filteredOpps.map((o) => [o.id, o]));
    return scopedResults
      .map((r) => {
        const opp = oppById.get(r.opportunityId);
        const fallbackSource =
          normalizeDisplayLabel(
            String((opp as { leadSource?: string } | undefined)?.leadSource ?? "").trim(),
            "Unattributed",
          ) || "Unattributed";
        return {
          ...r,
          assignedRep: resolveRepName(r.assignedRep),
          source: normalizeDisplayLabel(String(r.source ?? "").trim(), fallbackSource),
        };
      })
      .filter((r) => String(r.submittedAt || r.appointmentDatetime || "") >= bounds);
  }, [scopedResults, filteredOpps, bounds, repNameById]);
  const filteredFollowUps = useMemo(() => scopedFollowUps.filter((t) => (t.createdAt || "") >= bounds), [scopedFollowUps, bounds]);
  const hasFilteredData = filteredOpps.length > 0 || filteredResults.length > 0 || filteredFollowUps.length > 0;

  const getJobDate = (j: Job): string =>
    String(j.updatedAt ?? j.createdAt ?? j.scheduledInstallDate ?? "");

  const jobsInPeriod = useMemo(
    () => jobs.filter((j) => getJobDate(j) >= bounds),
    [jobs, bounds],
  );

  /* ── Revenue Quality (DB-backed) ── */
  const revenueQuality = useMemo(() => {
    const won = filteredOpps.filter((o) => o.stage === "Closed Won");
    const totalWon = won.length;
    const netJobsInPeriod = jobsInPeriod.filter(
      (j) =>
        j.currentStage === "Completed" ||
        j.rescissionStatus === "Cleared for Production",
    );
    const pending = won.filter((o) => !o.wonOutcome || o.wonOutcome === "pending");
    const totalLeads = filteredOpps.length;
    const wonRevenue = won.reduce((s, o) => s + getOpportunityDisplayValue(o, jobs), 0);
    const netRevenue = netJobsInPeriod.reduce((s, j) => s + getJobDisplayValue(j), 0);
    const pendingRevenue = pending.reduce((s, o) => s + getOpportunityDisplayValue(o, jobs), 0);
    const netCount = netJobsInPeriod.length;
    const netConversion = totalLeads > 0 ? Math.round((netCount / totalLeads) * 100) : 0;
    return { totalWon, netCount, pendingCount: pending.length, wonRevenue, netRevenue, pendingRevenue, netConversion, totalLeads };
  }, [filteredOpps, jobs, jobsInPeriod]);

  /* ── Rep Performance Intelligence (DB-backed) ── */
  const repInsights = useMemo(() => {
    return getRepPerformanceSummary(filteredResults, jobs).map((rep) => {
      const signal: "strong_on_quality" | "underperforming_on_quality" | "consistent" =
        rep.closeRate >= 50 ? "strong_on_quality" : rep.closeRate <= 20 ? "underperforming_on_quality" : "consistent";
      const insightSeverity: "positive" | "neutral" | "caution" =
        signal === "strong_on_quality" ? "positive" : signal === "underperforming_on_quality" ? "caution" : "neutral";
      const insight =
        signal === "strong_on_quality"
          ? "Strong performance on high-quality leads — efficient closer"
          : signal === "underperforming_on_quality"
          ? "Performance below expected range — coaching recommended"
          : "Performance within expected range — no anomalies detected";
      return {
        repName: resolveRepName(rep.rep),
        netConversion: rep.closeRate,
        highFitConversion: rep.closeRate,
        sampleSize: rep.total,
        signal,
        insightSeverity,
        insight,
      };
    }).slice(0, 5);
  }, [filteredResults, jobs]);

  /* ── Live Actions (parity with Classic) ── */
  const now = new Date();
  const actions = useMemo(() => {
    const missingResults = filteredOpps.filter(
      (o) => o.stage === "Appointment Completed" && !filteredResults.some((r) => r.opportunityId === o.id),
    );
    const overdueFollowUps = filteredFollowUps.filter(
      (t) => t.status !== "completed" && new Date(t.dueDate) < now,
    );
    const reschedulesNeeded = filteredResults.filter(
      (r) => r.resultType === "rescheduled" && !r.rescheduleDatetime,
    );
    return { missingResults, overdueFollowUps, reschedulesNeeded };
  }, [filteredOpps, filteredResults, filteredFollowUps]);
  const actionCount = actions.missingResults.length + actions.overdueFollowUps.length + actions.reschedulesNeeded.length;

  const channelMetrics = useMemo(() => {
    const netJobsInPeriod = jobsInPeriod.filter(
      (j) =>
        j.currentStage === "Completed" ||
        j.rescissionStatus === "Cleared for Production",
    );

    const sourceByOppId = new Map<string, string>();
    const sourceByJobId = new Map<string, string>();
    for (const opp of filteredOpps) {
      const source = normalizeDisplayLabel(
        String((opp as { leadSource?: string }).leadSource ?? "").trim(),
        "Unattributed",
      );
      sourceByOppId.set(opp.id, source);
      if (opp.linkedJobId) sourceByJobId.set(opp.linkedJobId, source);
    }

    const netJobsBySource = new Map<string, number>();
    for (const job of netJobsInPeriod) {
      const source =
        sourceByOppId.get(String(job.opportunityId ?? "")) ??
        sourceByJobId.get(job.id) ??
        "Unattributed";
      netJobsBySource.set(source, (netJobsBySource.get(source) ?? 0) + 1);
    }

    const bySource = new Map<string, { leads: number; sold: number; netJobs: number; sourceCost: number }>();
    for (const opp of filteredOpps) {
      const source = normalizeDisplayLabel(String((opp as { leadSource?: string }).leadSource ?? "").trim(), "Unattributed");
      const cur = bySource.get(source) ?? { leads: 0, sold: 0, netJobs: 0, sourceCost: 0 };
      cur.leads += 1;
      if (opp.stage === "Closed Won") cur.sold += 1;
      const cost = Number((opp as { leadCost?: number }).leadCost ?? 0);
      if (Number.isFinite(cost) && cost > 0) cur.sourceCost += cost;
      bySource.set(source, cur);
    }
    return Array.from(bySource.entries()).map(([source, row]) => {
      const netJobs = netJobsBySource.get(source) ?? 0;
      const leadQuality = row.leads > 0 ? Math.round((row.sold / row.leads) * 100) : 0;
      const acquisitionCount = netJobs > 0 ? netJobs : row.sold;
      const cpa =
        acquisitionCount > 0 && row.sourceCost > 0
          ? Math.round(row.sourceCost / acquisitionCount)
          : null;
      const status: ChannelStatus =
        leadQuality >= 70 ? "Healthy" : leadQuality >= 45 ? "Needs Attention" : "Underperforming";
      return {
        id: source.toLowerCase().replace(/\s+/g, "-"),
        name: source,
        cpa,
        leadQuality,
        leads: row.leads,
        netJobs,
        status,
      };
    });
  }, [filteredOpps, jobsInPeriod]);

  const sortedChannels = [...channelMetrics]
    .sort((a, b) => {
      const aCpa = a.cpa ?? Number.POSITIVE_INFINITY;
      const bCpa = b.cpa ?? Number.POSITIVE_INFINITY;
      return aCpa - bCpa;
    })
    .slice(0, 5);

  const bookingEfficiency = useMemo(() => {
    const scheduledJobs = jobs.filter((j) => Boolean(j.scheduledInstallDate));
    const totalBookings = scheduledJobs.length;

    const byDate = new Map<string, number>();
    scheduledJobs.forEach((j) => {
      const key = String(j.scheduledInstallDate ?? "");
      byDate.set(key, (byDate.get(key) ?? 0) + 1);
    });
    const clusteredDays = [...byDate.values()].filter((n) => n > 1).length;
    const clusterRatio = byDate.size > 0 ? clusteredDays / byDate.size : 0;
    const clusteringEfficiency: Clustering =
      clusterRatio >= 0.5 ? "High" : clusterRatio >= 0.25 ? "Medium" : "Low";

    const travelMetric = computeOutcomeMetrics({ jobs, wraps: installerWrapRecords }).find((m) => m.key === "travel_share");
    const longDistancePct = travelMetric?.value ?? 0;
    return {
      totalBookings,
      longDistancePct,
      longDistanceCount: Math.round((longDistancePct / 100) * totalBookings),
      clusteringEfficiency,
    };
  }, [jobs, installerWrapRecords]);
  const topReps = repInsights.slice(0, 5);

  /* ── System Recommendations — structured subject → action → outcome.
     Each rec carries an action context so the operator can Apply, Dismiss
     (with reason + snooze), or Log a touch — mirroring the homeowner
     interaction model. Dismissed recs are suppressed until snooze expires. ── */
  type Recommendation = {
    key: string;                 // stable id used to suppress dismissed recs
    channelId: string;           // partner / entity scope for history
    channelName: string;
    subject: string;
    action: string;
    outcome: string;
    severity: "positive" | "neutral" | "caution";
  };
  // Subscribe to channel-history changes so dismissals immediately re-render.
  const channelEvents = useChannelEvents();
  const recommendations = useMemo<Recommendation[]>(() => {
    void channelEvents; // re-evaluate when history changes
    const recs: Recommendation[] = [];
    const recommendationChannels = sortedChannels.filter((c) => c.leads >= 5 || c.netJobs >= 2);
    const topChannel = recommendationChannels[0];
    if (topChannel && topChannel.status === "Healthy") {
      recs.push({
        key: `channel:${topChannel.id}:increase-budget`,
        channelId: topChannel.id,
        channelName: topChannel.name,
        subject: topChannel.name,
        action: "Increase budget",
        outcome: `${topChannel.cpa !== null ? `$${topChannel.cpa} CPA` : "No cost data"} · ${topChannel.leadQuality}% quality`,
        severity: "positive",
      });
    }
    const worstChannel = [...recommendationChannels].sort((a, b) => a.leadQuality - b.leadQuality)[0];
    if (worstChannel && worstChannel.status === "Underperforming") {
      recs.push({
        key: `channel:${worstChannel.id}:review-spend`,
        channelId: worstChannel.id,
        channelName: worstChannel.name,
        subject: worstChannel.name,
        action: "Review spend",
        outcome: `${worstChannel.cpa !== null ? `$${worstChannel.cpa} CPA` : "No cost data"} · low quality`,
        severity: "caution",
      });
    }
    const topRep = repInsights.find((r) => r.sampleSize >= 3 && r.signal === "strong_on_quality");
    if (topRep) {
      recs.push({
        key: `rep:${topRep.repName}:route-high-fit`,
        channelId: `rep:${topRep.repName}`,
        channelName: topRep.repName,
        subject: topRep.repName,
        action: "Route more high-fit leads",
        outcome: `${topRep.highFitConversion}% high-fit conversion`,
        severity: "positive",
      });
    }
    const underRep = repInsights.find((r) => r.sampleSize >= 3 && r.signal === "underperforming_on_quality");
    if (underRep) {
      recs.push({
        key: `rep:${underRep.repName}:coach`,
        channelId: `rep:${underRep.repName}`,
        channelName: underRep.repName,
        subject: underRep.repName,
        action: "Coach on qualified leads",
        outcome: `${underRep.highFitConversion}% high-fit conversion`,
        severity: "caution",
      });
    }
    if (bookingEfficiency.longDistancePct > 15) {
      recs.push({
        key: `ops:booking-radius:tighten`,
        channelId: "ops:booking-radius",
        channelName: "Booking radius",
        subject: "Booking radius",
        action: "Tighten travel range",
        outcome: `${bookingEfficiency.longDistancePct}% exceed optimal`,
        severity: "caution",
      });
    }
    // Suppress dismissed recs (active snooze window).
    return recs.filter((r) => !isRecommendationDismissed(r.key)).slice(0, 4);
  }, [repInsights, channelEvents, sortedChannels, bookingEfficiency.longDistancePct]);

  /* ── Recommendation action modal state ── */
  const [recModalMode, setRecModalMode] = useState<RecActionMode>("apply");
  const [recModalContext, setRecModalContext] = useState<RecActionContext | null>(null);
  const openRecModal = (mode: RecActionMode, rec: Recommendation) => {
    setRecModalMode(mode);
    setRecModalContext({
      channelId: rec.channelId,
      channelName: rec.channelName,
      recommendationKey: rec.key,
      recommendationText: `${rec.subject} — ${rec.action} (${rec.outcome})`,
      decisionLabel: rec.action,
      severity: rec.severity,
    });
  };

  /** Mark a follow-up complete after the user logs the touch (DB first). */
  const handleLogClose = (open: boolean) => {
    if (open) return;
    const clearModalState = () => {
      setLogFollowUpId(null);
      setLogCustomerName(null);
      setLogRelatedJobId(undefined);
    };
    if (!logFollowUpId) {
      clearModalState();
      return;
    }
    const completeFollowUp = async () => {
      const completedAt = new Date().toISOString();
      const task = followUpTasks.find((t) => t.id === logFollowUpId);
      const completedBy = String(task?.assignedTo ?? "").trim() || "system";
      const { error } = await FollowUpTaskService.updateStatus(
        logFollowUpId,
        "completed",
        completedAt,
        completedBy,
      );
      if (error) {
        toast.error("Failed to save follow-up completion");
        return;
      }
      setFollowUpTasks((prev) =>
        prev.map((t) =>
          t.id === logFollowUpId
            ? { ...t, status: "completed" as const, completedAt }
            : t,
        ),
      );
      clearModalState();
    };
    void completeFollowUp();
  };

  /** Push Mode runner (parity with Classic). */
  const runAction = (
    actionType: "missing-result" | "overdue-followup" | "reschedule",
    payload: { customerName?: string; jobId?: string; followUpId?: string; job?: Job | null },
  ) => {
    if (actionType === "missing-result") {
      if (!payload.customerName) { toast.error("No record available to log"); return; }
      setLogFollowUpId(null);
      setLogRelatedJobId(payload.jobId);
      setLogCustomerName(payload.customerName);
      return;
    }
    if (actionType === "overdue-followup") {
      if (!payload.customerName || !payload.followUpId) { toast.error("No follow-up available"); return; }
      setLogRelatedJobId(payload.jobId);
      setLogFollowUpId(payload.followUpId);
      setLogCustomerName(payload.customerName);
      return;
    }
    if (actionType === "reschedule") {
      if (!payload.job) { toast.error("Linked job not found — open Appointment Results"); return; }
      setRescheduleJob(payload.job);
      return;
    }
  };

  /* ── "Today's score" — the one-line answer to "how am I doing?".
     Booked = won revenue in period. At risk = pending-outcome revenue
     (the same dollar figure surfaced by Revenue Quality). Installs
     scheduled = jobs with a scheduledInstallDate inside the period. ── */
  const installsScheduled = useMemo(() => {
    return jobs.filter((j) => {
      if (!j.scheduledInstallDate) return false;
      return j.scheduledInstallDate >= bounds;
    }).length;
  }, [jobs, bounds]);

  /* ── Build the grouped Live Actions list — strict parity with Classic.
     Three rows: Missing Results, Overdue Follow-Ups, Reschedules Needed.
     Each shows count + single CTA that resolves the top item in place.
     This MUST mirror Classic exactly — no per-item expansion, no added
     labels, no added actions. ── */
  /**
   * Each attention group surfaces the *top item* with full context:
   *   • who (customerName)
   *   • why (one-line reason — disposition, notes, or appointment context)
   *   • how to reach (phone, when known)
   *
   * Primary CTA is "Call" — the operator dials immediately and the Log
   * modal opens behind it so they can capture the outcome the moment the
   * call ends. Secondary CTA = "Log" alone (no dial). Reschedule rows
   * keep their own primary because dialing without a time slot is noise.
   */
  type AttentionGroup = {
    id: "missing-results" | "overdue-followups" | "reschedules-needed";
    label: string;
    count: number;
    severity: "critical" | "warn" | "info";
    /** Top-item context revealed inline. */
    topName?: string;
    topReason?: string;
    topPhone?: string;
    /** Primary CTA — the one-tap action. */
    primaryCta: string;
    onPrimary: () => void;
    /** Secondary CTA — quieter alternate. Optional. */
    secondaryCta?: string;
    onSecondary?: () => void;
    /** Empty-state helper. */
    emptyHint: string;
  };

  const attention: AttentionGroup[] = useMemo(() => {
    const callAndLog = (customerName: string, phone: string | undefined, followUpId: string | null, jobId?: string) => {
      if (phone) {
        window.open(`tel:${phone}`, "_self");
        toast.info(`Calling ${customerName} — log the outcome when you're done.`);
      } else {
        toast.info(`No phone on file for ${customerName} — opening log only.`);
      }
      // Open log modal behind the call so the rep captures outcome immediately.
      setLogRelatedJobId(jobId);
      setLogFollowUpId(followUpId);
      setLogCustomerName(customerName);
    };

    const topMissing = actions.missingResults[0];
    const topFollowUp = actions.overdueFollowUps[0];
    const topReschedule = actions.reschedulesNeeded[0];

    const missingPhone = topMissing
      ? jobs.find((j) => j.customerName === topMissing.customerName)?.phone
      : undefined;
    const followUpReason =
      topFollowUp?.dispositionReason ||
      topFollowUp?.notes ||
      (topFollowUp?.disposition ? `Disposition: ${topFollowUp.disposition}` : "Overdue — no context captured");
    const reschedulePhone = topReschedule
      ? jobs.find((j) => j.customerName === topReschedule.customerName)?.phone
      : undefined;

    const groups: AttentionGroup[] = [
      {
        id: "missing-results",
        label: "Missing Results",
        count: actions.missingResults.length,
        severity: actions.missingResults.length > 0 ? "warn" : "info",
        topName: topMissing?.customerName,
        topReason: topMissing
          ? "Appointment completed — outcome not captured yet"
          : undefined,
        topPhone: missingPhone,
        primaryCta: missingPhone ? "Call & log" : "Log outcome",
        onPrimary: () => {
          if (!topMissing) return;
          callAndLog(topMissing.customerName, missingPhone, null);
        },
        secondaryCta: "Log only",
        onSecondary: () => topMissing && runAction("missing-result", { customerName: topMissing.customerName }),
        emptyHint: "Every appointment has an outcome",
      },
      {
        id: "overdue-followups",
        label: "Overdue Follow-Ups",
        count: actions.overdueFollowUps.length,
        severity: actions.overdueFollowUps.length >= 3 ? "critical" : actions.overdueFollowUps.length > 0 ? "warn" : "info",
        topName: topFollowUp?.customerName,
        topReason: topFollowUp ? followUpReason : undefined,
        topPhone: topFollowUp?.customerPhone,
        primaryCta: topFollowUp?.customerPhone ? "Call & log" : "Log touch",
        onPrimary: () => {
          if (!topFollowUp) return;
          callAndLog(topFollowUp.customerName, topFollowUp.customerPhone, topFollowUp.id);
        },
        secondaryCta: "Log only",
        onSecondary: () => topFollowUp && runAction("overdue-followup", { customerName: topFollowUp.customerName, followUpId: topFollowUp.id }),
        emptyHint: "All follow-ups current",
      },
      {
        id: "reschedules-needed",
        label: "Reschedules Needed",
        count: actions.reschedulesNeeded.length,
        severity: actions.reschedulesNeeded.length > 0 ? "warn" : "info",
        topName: topReschedule?.customerName,
        topReason: topReschedule
          ? "Appointment marked rescheduled — no new time set"
          : undefined,
        topPhone: reschedulePhone,
        primaryCta: "Reschedule",
        onPrimary: () => {
          if (!topReschedule) return;
          const linkedJob = jobs.find((j) => j.customerName === topReschedule.customerName) ?? null;
          runAction("reschedule", { job: linkedJob });
        },
        secondaryCta: reschedulePhone ? "Call first" : undefined,
        onSecondary: reschedulePhone
          ? () => {
              window.open(`tel:${reschedulePhone}`, "_self");
              toast.info(`Calling ${topReschedule!.customerName} to confirm a new time.`);
            }
          : undefined,
        emptyHint: "No reschedules pending",
      },
    ];
    return groups;
     
  }, [actions, jobs]);

  return (
    <div className="space-y-10 max-w-[1600px] mx-auto w-full">
      {/* ── Header — title is owned by the cockpit masthead; we render only
          the period summary line + PeriodSwitch to avoid duplicating
          "Command Dashboard" on the page. ── */}
      <header className="flex items-end justify-between gap-6">
        <p className="text-[13px] text-muted-foreground tabular-nums">
          {periodLabel} · <span className="text-foreground font-medium">{fmt(revenueQuality.wonRevenue)}</span> Booked
          {revenueQuality.pendingRevenue > 0 && (
            <> · <button
              onClick={() => navigate("/intelligence")}
              className="group inline-flex items-baseline gap-1 text-[hsl(var(--warning))] font-medium hover:underline underline-offset-4 decoration-[hsl(var(--warning))]/60 transition-colors"
              title="View revenue leak detail"
            >
              {fmt(revenueQuality.pendingRevenue)}
              <span aria-hidden className="text-[10px] opacity-60 group-hover:opacity-100 transition-opacity">→</span>
            </button> at Risk</>
          )}
          {" · "}<span className="text-foreground font-medium">{installsScheduled}</span> Install{installsScheduled === 1 ? "" : "s"} Scheduled
        </p>
        <PeriodSwitch value={period} onChange={setPeriod} />
      </header>


      {/* ── Active filter banner (parity with Classic, redrawn quietly) ── */}
      {!hasFilteredData && (
        <p className="text-[12px] text-[hsl(var(--warning))]">
          No opportunities, results, or follow-ups in this range — try a wider window.
        </p>
      )}

      {/* ── #2 STAT STRIP — the KPI row, hairline-divided, no cards ── */}
      <StatStrip
        items={[
          { label: "Net Conversion", value: `${revenueQuality.netConversion}%`, sub: `${revenueQuality.netCount}/${revenueQuality.totalLeads || 0}` },
          { label: "Net Revenue", value: fmt(revenueQuality.netRevenue), sub: `${revenueQuality.netCount} Net` },
          { label: "Pending Revenue", value: fmt(revenueQuality.pendingRevenue), sub: `${revenueQuality.pendingCount} Pending`, accent: revenueQuality.pendingCount > 0 ? "warn" : undefined },
          {
            label: "Avg CPA · Channels",
            value: (() => {
              const cpas = channelMetrics
                .map((c) => c.cpa)
                .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
              return cpas.length > 0 ? `$${Math.round(cpas.reduce((s, v) => s + v, 0) / cpas.length)}` : "—";
            })(),
            sub: `${channelMetrics.filter((c) => typeof c.cpa === "number" && Number.isFinite(c.cpa)).length} Active`,
          },
          { label: "Long-Distance", value: `${bookingEfficiency.longDistancePct}%`, sub: `${bookingEfficiency.longDistanceCount} of ${bookingEfficiency.totalBookings}`, accent: bookingEfficiency.longDistancePct > 15 ? "warn" : undefined },
        ]}
      />

      {/* ── Live intelligence strip (preserved; renders cockpit-skinned variant) ── */}
      <WhatNeedsAttentionStrip />

      {/* ── #1 HERO — Live Actions + System Recommendations side-by-side.
          The action list owns the page; recommendations are the quiet
          guidance layer that makes the app feel intelligent. ── */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        {/* Live Actions — the hero */}
        <div>
          <SectionHeader
            title="Live Actions"
            eyebrow={`${actionCount} Live Action${actionCount === 1 ? "" : "s"}`}
          />
          <div className="rounded-[10px] border border-border bg-card overflow-hidden">
            {actionCount === 0 ? (
              <div className="px-5 py-10 text-center">
                <p className="text-[15px] text-foreground">You're caught up for {periodLabel.toLowerCase()}.</p>
                <p className="text-[13px] text-muted-foreground mt-1">
                  Every appointment has an outcome, every follow-up is current, every reschedule has a time.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {attention.map((group, i) => {
                  const firstActive = attention.findIndex((g) => g.count > 0);
                  return (
                    <AttentionRow key={group.id} group={group} primary={i === firstActive} />
                  );
                })}
              </ul>
            )}
            {/* Escape-hatch links removed — the three attention rows above already
                own navigation to Opportunities, Follow-Ups, and Reschedules via
                their primary/secondary CTAs. The duplicated footer strip felt
                redundant and visually unbalanced the panel. */}
          </div>
        </div>

        {/* Recommendations — the guidance layer */}
        <div>
          <SectionHeader title="Recommended" eyebrow="System Guidance" />
          <div className="rounded-[10px] border border-border bg-card overflow-hidden">
            {recommendations.length === 0 ? (
              <p className="px-5 py-10 text-center text-[13px] text-muted-foreground">
                Nothing to recommend — performance is on track.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {recommendations.map((rec) => (
                  <RecommendationRow
                    key={rec.key}
                    rec={rec}
                    onApply={() => openRecModal("apply", rec)}
                    onDismiss={() => openRecModal("dismiss", rec)}
                    onLogTouch={() => openRecModal("touch", rec)}
                  />
                ))}
              </ul>
            )}
          </div>
          {/* Recovery affordance — only renders if there are active dismissals. */}
          <DismissedRecsDrawer />
        </div>
      </section>

      {/* ── Revenue Quality — Won / Pending / Net split (parity with Classic) ── */}
      <section>
        <SectionHeader title="Revenue Quality" eyebrow={periodLabel} />
        <div className="grid grid-cols-3 border-y border-border tabular-nums">
          <RevenueCell label="Won" count={revenueQuality.totalWon} amount={revenueQuality.wonRevenue} />
          <RevenueCell label="Pending" count={revenueQuality.pendingCount} amount={revenueQuality.pendingRevenue} accent="warn" border />
          <RevenueCell label="Net" count={revenueQuality.netCount} amount={revenueQuality.netRevenue} accent="good" border />
        </div>
        <p className="text-[12px] text-muted-foreground mt-2.5">
          Net represents finalized jobs after cancellations and financing outcomes.
        </p>
      </section>

      {/* ── Channels (left) + Reps (right) — ranked lists ── */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
        {/* Channels */}
        <div>
          <SectionHeader
            title="Growth Performance"
            actionLabel="All Channels"
            onAction={() => navigate("/growth-channels")}
          />
          <ul className="divide-y divide-border border-y border-border">
            {sortedChannels.map((ch, i) => (
              <RankedRow
                key={ch.id}
                rank={i + 1}
                primary={ch.name}
                secondary={`${ch.leads} Leads · ${ch.netJobs} Net · ${ch.leadQuality}% Quality`}
                value={ch.cpa !== null ? `$${ch.cpa}` : "—"}
                valueLabel="CPA"
                status={ch.status === "Healthy" ? "good" : ch.status === "Needs Attention" ? "warn" : "bad"}
              />
            ))}
          </ul>
        </div>

        {/* Reps */}
        {fin$.viewRepLeaderboard && (
          <div>
            <SectionHeader
              title="Rep Performance"
              actionLabel="Open Rep Scorecards →"
              onAction={() => navigate("/rep-scorecards")}
            />
            <ul className="divide-y divide-border border-y border-border">
              {topReps.map((rep, i) => (
                <RankedRow
                  key={rep.repName}
                  rank={i + 1}
                  primary={rep.repName}
                  secondary={rep.insight || `${rep.highFitConversion}% on High-Fit Leads`}
                  value={`${rep.netConversion}%`}
                  valueLabel={`${rep.highFitConversion}% HF`}
                  status={
                    rep.insightSeverity === "positive" ? "good" :
                    rep.insightSeverity === "caution" ? "warn" : "neutral"
                  }
                />
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* ── Booking Efficiency — preserved as a quiet stat row ── */}
      <section>
        <SectionHeader
          title="Booking Efficiency"
          actionLabel="View Calendar"
          onAction={() => navigate("/scheduling-calendar")}
        />
        <div className="grid grid-cols-3 border-y border-border tabular-nums">
          <MiniStat label="Avg Travel" value="—" />
          <MiniStat
            label="Long-Distance"
            value={`${bookingEfficiency.longDistancePct}%`}
            accent={bookingEfficiency.longDistancePct > 15 ? "warn" : undefined}
            border
          />
          <MiniStat
            label="Clustering"
            value={bookingEfficiency.clusteringEfficiency}
            accent={
              bookingEfficiency.clusteringEfficiency === "High" ? "good" :
              bookingEfficiency.clusteringEfficiency === "Medium" ? "warn" : "bad"
            }
            border
          />
        </div>
        <p className="text-[12px] text-muted-foreground mt-2.5">
          {bookingEfficiency.longDistanceCount} of {bookingEfficiency.totalBookings} bookings exceed optimal travel range.
        </p>
      </section>

      {/* ═══ SALES FUNNEL BREAKDOWN (moved from Intelligence Dashboard) ═══ */}
      <SalesFunnelBreakdown />

      {/* ── Push Mode modals ── */}
      <LogInteractionModal
        open={!!logCustomerName}
        onOpenChange={handleLogClose}
        customerName={logCustomerName ?? ""}
        relatedJobId={logRelatedJobId}
      />
      <RescheduleModal
        open={!!rescheduleJob}
        onOpenChange={(o) => !o && setRescheduleJob(null)}
        job={rescheduleJob}
      />
      <RecommendationActionModal
        open={!!recModalContext}
        onOpenChange={(o) => !o && setRecModalContext(null)}
        mode={recModalMode}
        context={recModalContext}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * Local primitives
 * NOTE: SectionHeader, StatStrip, and StatItem moved to
 * @/components/dashboard/primitives so OperationsCommandCenter (Cockpit)
 * can share the same look. Imported at the top of this file.
 * ───────────────────────────────────────────────────────────────────── */

function PeriodSwitch({ value, onChange }: { value: Period; onChange: (p: Period) => void }) {
  const opts: { id: Period; label: string }[] = [
    { id: "today", label: "Today" },
    { id: "week", label: "7 Days" },
    { id: "month", label: "30 Days" },
  ];
  return (
    <div className="inline-flex items-center text-[13px]" role="tablist">
      {opts.map((o, i) => (
        <button
          key={o.id}
          role="tab"
          aria-selected={value === o.id}
          onClick={() => onChange(o.id)}
          className={cn(
            "px-2.5 py-1 transition-colors",
            value === o.id ? "text-foreground font-semibold" : "text-muted-foreground hover:text-foreground",
            i > 0 && "border-l border-border",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function AttentionRow({
  group,
  primary,
}: {
  group: {
    label: string;
    count: number;
    severity: "critical" | "warn" | "info";
    topName?: string;
    topReason?: string;
    topPhone?: string;
    primaryCta: string;
    onPrimary: () => void;
    secondaryCta?: string;
    onSecondary?: () => void;
    emptyHint: string;
  };
  primary: boolean;
}) {
  const empty = group.count === 0;
  const dot = empty
    ? "bg-[hsl(var(--success))]"
    : group.severity === "critical" ? "bg-destructive"
    : group.severity === "warn" ? "bg-[hsl(var(--warning))]"
    : "bg-muted-foreground/40";

  // Format phone for context line — last 4 digits keeps it scannable.
  const phoneTail = group.topPhone ? group.topPhone.replace(/\D/g, "").slice(-4) : "";

  return (
    <li className="flex items-start gap-4 px-5 py-3.5 hover:bg-muted/40 transition-colors">
      <span className={cn("h-1.5 w-1.5 rounded-full shrink-0 mt-[7px]", dot)} aria-hidden />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="text-[15px] text-foreground">{group.label}</span>
          <span className="text-[13px] text-muted-foreground tabular-nums">{group.count}</span>
        </div>
        {empty ? (
          <div className="text-[13px] text-muted-foreground truncate">{group.emptyHint}</div>
        ) : (
          <div className="text-[13px] text-muted-foreground truncate mt-0.5">
            {group.topName && (
              <span className="text-foreground font-medium">{group.topName}</span>
            )}
            {group.topReason && (
              <>
                {group.topName && " — "}
                <span>{group.topReason}</span>
              </>
            )}
            {phoneTail && (
              <span className="text-muted-foreground/70 tabular-nums"> · ··{phoneTail}</span>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {!empty && group.secondaryCta && group.onSecondary && (
          <button
            onClick={group.onSecondary}
            className="text-[13px] px-2.5 py-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            {group.secondaryCta}
          </button>
        )}
        <button
          onClick={group.onPrimary}
          disabled={empty}
          className={cn(
            "text-[13px] px-3 py-1.5 rounded-md transition-colors",
            empty
              ? "text-muted-foreground/50 cursor-not-allowed"
              : primary
              ? "bg-foreground text-background hover:bg-foreground/90 font-semibold"
              : "text-foreground hover:bg-muted",
          )}
        >
          {group.primaryCta}
        </button>
      </div>
    </li>
  );
}

function RecommendationRow({
  rec,
  onApply,
  onDismiss,
  onLogTouch,
}: {
  rec: {
    subject: string;
    action: string;
    outcome: string;
    severity: "positive" | "neutral" | "caution";
  };
  onApply: () => void;
  onDismiss: () => void;
  onLogTouch: () => void;
}) {
  const dot =
    rec.severity === "positive" ? "bg-[hsl(var(--success))]" :
    rec.severity === "caution" ? "bg-[hsl(var(--warning))]" :
    "bg-muted-foreground/40";
  return (
    <li className="flex items-start gap-4 px-5 py-3.5 hover:bg-muted/40 transition-colors">
      <span className={cn("h-1.5 w-1.5 rounded-full shrink-0 mt-[7px]", dot)} aria-hidden />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] leading-snug">
          <span className="font-semibold text-foreground">{rec.subject}</span>
          <span className="text-foreground"> — {rec.action}</span>
        </div>
        <div className="text-[12px] text-muted-foreground tabular-nums truncate mt-0.5">{rec.outcome}</div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <button
          type="button"
          onClick={onLogTouch}
          className="text-[13px] px-2.5 py-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          Log touch
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="text-[13px] px-2.5 py-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          Dismiss
        </button>
        <button
          type="button"
          onClick={onApply}
          className="text-[13px] px-3 py-1.5 rounded-md bg-foreground text-background hover:bg-foreground/90 font-semibold transition-colors"
        >
          Apply
        </button>
      </div>
    </li>
  );
}

function RevenueCell({
  label,
  count,
  amount,
  accent,
  border,
}: {
  label: string;
  count: number;
  amount: number;
  accent?: "good" | "warn";
  border?: boolean;
}) {
  return (
    <div className={cn("px-5 py-4", border && "border-l border-border")}>
      <div className="text-[11px] uppercase tracking-[0.06em] text-muted-foreground font-medium">{label}</div>
      <div className="mt-1.5 flex items-baseline gap-2">
        <span
          className={cn(
            "text-[22px] font-semibold tracking-[-0.018em] leading-none",
            accent === "good" ? "text-[hsl(var(--success))]" :
            accent === "warn" ? "text-[hsl(var(--warning))]" :
            "text-foreground",
          )}
        >
          {count}
        </span>
        <span className="text-[13px] text-muted-foreground">{fmt(amount)}</span>
      </div>
    </div>
  );
}

function MiniStat({
  label,
  value,
  accent,
  border,
}: {
  label: string;
  value: string;
  accent?: "good" | "warn" | "bad";
  border?: boolean;
}) {
  return (
    <div className={cn("px-5 py-4", border && "border-l border-border")}>
      <div className="text-[11px] uppercase tracking-[0.06em] text-muted-foreground font-medium">{label}</div>
      <div
        className={cn(
          "mt-1.5 text-[22px] font-semibold tracking-[-0.018em] leading-none",
          accent === "good" ? "text-[hsl(var(--success))]" :
          accent === "warn" ? "text-[hsl(var(--warning))]" :
          accent === "bad" ? "text-destructive" :
          "text-foreground",
        )}
      >
        {value}
      </div>
    </div>
  );
}


function RankedRow({
  rank,
  primary,
  secondary,
  value,
  valueLabel,
  status,
}: {
  rank: number;
  primary: string;
  secondary: string;
  value: string;
  valueLabel: string;
  status: "good" | "warn" | "bad" | "neutral";
}) {
  const dot =
    status === "good" ? "bg-[hsl(var(--success))]" :
    status === "warn" ? "bg-[hsl(var(--warning))]" :
    status === "bad" ? "bg-destructive" :
    "bg-muted-foreground/30";
  return (
    <li className="flex items-center gap-4 px-1 py-3.5">
      <span className="text-[12px] text-muted-foreground font-medium tabular-nums w-4 text-right">{rank}</span>
      <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", dot)} aria-hidden />
      <div className="flex-1 min-w-0">
        <div className="text-[15px] text-foreground truncate leading-tight">{primary}</div>
        <div className="text-[12px] text-muted-foreground truncate mt-0.5">{secondary}</div>
      </div>
      <div className="text-right tabular-nums shrink-0">
        <div className="text-[15px] font-semibold text-foreground leading-none">{value}</div>
        <div className="text-[11px] uppercase tracking-[0.06em] text-muted-foreground font-medium mt-1.5">{valueLabel}</div>
      </div>
    </li>
  );
}
